import type { BiConfigType, SiteColorMap } from '../../types';
import type { ThemeColor, Color } from './types';
import colorParser from 'color';
import {
  convertHexaOrHexToRGBAUnits,
  isValidColor as isValidColorUtil,
} from '@wix/editor-elements-common-utils';
import type { EditorStoreActions } from '@wix/editor-elements-types/editor';

export const TRANSPARENT_COLOR: Color = {
  propertyType: 'Color',
  value: 'transparent',
};

const getThemeFromLegacyColorString = (value: string): ThemeColor['theme'] => {
  const colorVarMatches = value.match(/(color_\d+)\)/);
  return colorVarMatches![1] as ThemeColor['theme'];
};

const getOpacityFromLegacyColorString = (value: string): number => {
  const opacityMatches = value.match(/,[ ]?(\d+(\.\d*)?)\)$/);
  return parseFloat(opacityMatches?.[1] ?? '1');
};

export const toThemeColor = (
  value: string,
  explicitAlpha?: number,
): ThemeColor | undefined => {
  if (isThemeColorWithVar(value)) {
    const theme = getThemeFromLegacyColorString(value);
    const alpha = getOpacityFromLegacyColorString(value);
    const themeColor: ThemeColor = {
      propertyType: 'ThemeColor',
      theme,
    };
    if (alpha < 1) {
      themeColor.alpha = {
        propertyType: 'Opacity',
        value: alpha,
      };
    }
    return themeColor;
  } else if (isThemeColor(value)) {
    const themeColor: ThemeColor = {
      propertyType: 'ThemeColor',
      theme: value as ThemeColor['theme'],
    };
    if (
      explicitAlpha !== null &&
      explicitAlpha !== undefined &&
      explicitAlpha < 1
    ) {
      themeColor.alpha = {
        propertyType: 'Opacity',
        value: explicitAlpha,
      };
    }
    return themeColor;
  } else {
    return;
  }
};

export const toColor = (value: string, explicitAlpha?: number): Color => {
  const { r, g, b, alpha: parsedColorAlpha } = colorParser(value).object();
  const alpha =
    explicitAlpha !== null && explicitAlpha !== undefined && explicitAlpha < 1
      ? explicitAlpha
      : parsedColorAlpha;
  const hexColor = colorParser([
    r,
    g,
    b,
    parseFloat(alpha?.toFixed(2) || '1'),
  ]).hexa();

  return {
    propertyType: 'Color',
    value: hexColor,
  };
};

export const toColorOrThemeColor = (
  value: string,
  explicitAlpha?: number,
): Color | ThemeColor | undefined => {
  try {
    const themeColor = toThemeColor(value, explicitAlpha);
    if (themeColor) {
      return themeColor;
    }

    return toColor(value, explicitAlpha);
  } catch {
    return;
  }
};

export const isThemeColorWithVar = (value: string) => {
  const isRgbWithVar = /^rgb\(var\(--color_\d+\)\)$/.test(value);
  const isRgbaWithVar = /^rgba\(var\(--color_\d+\),[ ]?\d+(\.\d*)?\)$/.test(
    value,
  );
  return isRgbWithVar || isRgbaWithVar;
};

export const isValidColor = (value: string) => isValidColorUtil(value);

export const fixThemeColorValue = (colorVal: string) => {
  const match = colorVal.match(/^{(?<themeColor>color_\d+)}$/);
  const themeColor = match?.groups!.themeColor;
  return themeColor ? themeColor : colorVal;
};

export const fixDuplicateRgbValue = (colorVal: string) => {
  const match = colorVal.match(/rgb\(rgb\((?<param>\d+,\d+,\d+)\)/);
  const rgbValue = match?.groups!.param;
  return rgbValue ? `rgb(${rgbValue})` : colorVal;
};

export const parseThemeWithOpacity = (value: string) => {
  const colorVarMatches = value.match(/(color_\d+)\)/);
  const opacityMatches = value.match(/,[ ]?(\d+(\.\d*)?)\)$/);
  return {
    theme: colorVarMatches![1],
    alpha: parseFloat(opacityMatches?.[1] ?? '1'),
  };
};

export const isThemeColor = (value: string) => /^color_\d+$/.test(value);

export const stringifyColor = (color: Color | ThemeColor): string => {
  if (color.propertyType === 'ThemeColor') {
    return `rgba(var(--${color.theme}),${color?.alpha?.value ?? 1})`;
  } else {
    const parser = colorParser(color.value);
    // @ts-expect-error
    const { color: parsedColor } = parser.rgb();

    return `rgba(${parsedColor[0]},${parsedColor[1]},${parsedColor[2]},${
      parser.alpha() ?? 1
    })`;
  }
};

export const stringifyThemeColor = (themeColor: ThemeColor): string => {
  if (!themeColor?.alpha) {
    return themeColor.theme;
  }
  return stringifyColor(themeColor);
};

export const getAlpha = (color: Color | ThemeColor): number => {
  let alpha;
  if (color.propertyType === 'ThemeColor') {
    alpha = color?.alpha?.value;
  } else {
    alpha = colorParser(color.value).alpha();
  }

  return alpha ?? 1;
};

export const getColorValue = (color: Color | ThemeColor) =>
  color.propertyType === 'Color' ? color.value : color.theme;

export const getCssColor = (
  color: ThemeColor | Color,
  siteColorMap: SiteColorMap,
): string => {
  if (typeof color === 'object' && color.propertyType === 'ThemeColor') {
    const siteColor = siteColorMap[color.theme];
    const [r, g, b, a] = convertHexaOrHexToRGBAUnits(siteColor) ?? [];
    return `rgba(${r},${g},${b},${a ?? 1})`;
  }
  return color.value;
};

export const getColorString = (color: ThemeColor | Color | string): string => {
  if (typeof color === 'string') {
    return color;
  }
  if (color.propertyType === 'ThemeColor') {
    return color.theme;
  }
  return color.value;
};

export type OpenColorPickerType = ReturnType<typeof generateOpenColorPicker>;

export const generateOpenColorPicker = (
  openColorPicker: EditorStoreActions.OpenColorPickerFunction,
) => {
  return (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>,
    value: Color | ThemeColor | string,
    onChange: (valueToUpdate: any, biConfig?: BiConfigType) => void,
    biConfig?: BiConfigType,
  ) => {
    const { clientY, clientX } = event;
    const stringValue = getColorString(value);
    const onChangeInner = (newValue: any) => onChange(newValue, biConfig);
    return openColorPicker?.(
      stringValue,
      { top: clientY, left: clientX },
      { onChange: onChangeInner },
    );
  };
};
